import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, MapPin, ArrowRight, Loader2, Search as SearchIcon } from "lucide-react";
import { proApi } from "@/lib/proApi";
import { fmtDateTime } from "@/lib/api";

const ProCourses = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyUpcoming, setOnlyUpcoming] = useState(true);

  useEffect(() => { proApi.get("/pro/courses").then((r) => setData(r.data)).catch(() => {}); }, []);

  const courses = useMemo(() => {
    if (!data) return [];
    const now = new Date();
    const nextStart = (c) => {
      const up = (c.sessions || [])
        .filter((s) => s.status !== "cancelled" && new Date(s.start) >= now)
        .sort((a, b) => new Date(a.start) - new Date(b.start));
      const s = up[0] || (c.sessions || []).slice().sort((a, b) => new Date(a.start) - new Date(b.start))[0];
      return s ? new Date(s.start).getTime() : Number.POSITIVE_INFINITY;
    };
    return data.courses
      .filter((c) => {
        if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
        if (onlyUpcoming) {
          const hasUpcoming = (c.sessions || []).some((s) => s.status !== "cancelled" && new Date(s.start) >= now);
          if (!hasUpcoming) return false;
        }
        return true;
      })
      .sort((a, b) => nextStart(a) - nextStart(b));
  }, [data, q, onlyUpcoming]);

  const nextSession = (c) => {
    const now = new Date();
    const up = (c.sessions || []).filter((s) => s.status !== "cancelled" && new Date(s.start) >= now).sort((a, b) => new Date(a.start) - new Date(b.start));
    return up[0] || (c.sessions || [])[0];
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#ff0033]" /></div>;

  return (
    <div data-testid="pro-courses">
      <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-5">Kurse</h1>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input data-testid="pro-courses-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kursname suchen…"
            className="w-full bg-[#1A1A1A] border border-white/10 text-white pl-9 pr-3 py-2.5 focus:border-[#ff0033] outline-none text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer"><input type="checkbox" checked={onlyUpcoming} onChange={(e) => setOnlyUpcoming(e.target.checked)} className="accent-[#ff0033]" /> Nur kommende</label>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map((c) => {
          const ns = nextSession(c);
          return (
            <button key={c.id} onClick={() => nav(`/pro/courses/${c.id}`)} data-testid={`pro-course-${c.id}`}
              className="text-left bg-[#0F0F0F] border border-white/10 hover:border-[#ff0033]/50 transition-colors p-5 flex flex-col">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono-lab text-[9px] uppercase tracking-widest text-[#ff0033] border border-[#ff0033]/40 px-2 py-0.5">{c.category || "Kurs"}</span>
              </div>
              <h3 className="font-heading text-xl uppercase tracking-tight text-white mt-3 leading-tight">{c.name}</h3>
              <div className="mt-3 text-xs text-neutral-400 space-y-1.5 flex-1">
                <span className="flex items-center gap-2"><MapPin size={13} className="text-[#ff0033]" /> {c.location || "—"}</span>
                <span className="flex items-center gap-2"><Users size={13} className="text-[#ff0033]" /> {c.participant_count} Teilnehmer</span>
                {ns && <span className="block text-neutral-500">Nächster Termin: {fmtDateTime(ns.start)}</span>}
              </div>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[#ff0033] font-heading uppercase text-xs tracking-widest">Öffnen <ArrowRight size={13} /></span>
            </button>
          );
        })}
      </div>
      {courses.length === 0 && <p className="text-neutral-500 mt-6">Keine Kurse gefunden.</p>}
    </div>
  );
};

export default ProCourses;
